import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getApiErrorUserMessage, readApiError } from "_shared/_errors/api-error";
import { getApiErrorData, getApiErrorStatus } from "_shared/_errors/api-error-message";
import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { api } from "_shared/_stores/api";
import Guid from "_shared/guids/guid";
import AcceptEntityUserInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/accept-entity-user-invitation-api-command";
import CreateUserEntityMemberInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/create-user-entity-member-invitation-api-command";
import UserEntityMemberInvitationSchema, {
  UserEntityMemberInvitationJSON,
} from "legal-entities/invitations/member-invitations/email-based/_schema/user-entity-member-invitation-schema";
import UserEntityMemberInvitation, {
  InvitationForAnotherAccountError,
  InvitationLinkInvalidError,
  InvitationRequiresSignInError,
  MemberInvitationNotOpenError,
  UserMemberInvitationNotFoundError,
} from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import Session from "users/__session/session";

type UserEntityMemberInvitationIdArgs = {
  id: Guid;
};

/**
 * The columns `GET legal-entities/{entityId}/user-entity-member-invitations` sorts on, spelled as the
 * API's `UserEntityMemberInvitationOrderField` is.
 */
export enum UserEntityMemberInvitationOrderField {
  DateSent = "DateSent",
  Name = "Name",
}

/** The two statuses an active invitation can have, and so the only two the list filters on. */
export type ActiveInvitationStatus = InvitationStatus.Open | InvitationStatus.Expired;

/**
 * What one page of an organisation's active invitations is asked for with (#5444).
 *
 * Every field goes to the server. The server's defaults are newest first, every active
 * invitation, and the first page of 25.
 */
export type UserEntityMemberInvitationListQuery = {
  /** Part of the invitee's first name, last name, both, or email address. */
  search?: string;
  status?: ActiveInvitationStatus;
  orderBy?: UserEntityMemberInvitationOrderField;
  orderDir?: OrderDirection;
  pageIndex?: number;
  pageSize?: number;
};

type GetActiveUserEntityMemberInvitationsArgs = UserEntityMemberInvitationListQuery & {
  entityId: Guid;
};

/** The query string for one page of active invitations. A blank search is left off rather than sent. */
export function userEntityMemberInvitationListParams(query: UserEntityMemberInvitationListQuery): URLSearchParams {
  const params = new URLSearchParams();
  const search = query.search?.trim();
  if (search) params.set("search", search);
  if (query.status) params.set("status", query.status);
  if (query.orderBy) params.set("orderBy", query.orderBy);
  if (query.orderDir) params.set("orderDir", query.orderDir);
  if (query.pageIndex !== undefined) params.set("pageIndex", `${query.pageIndex}`);
  if (query.pageSize !== undefined) params.set("pageSize", `${query.pageSize}`);
  return params;
}

type CreateUserEntityMemberInvitationArgs = {
  accessTemplateId?: Guid;
  invitation: UserEntityMemberInvitation;
};

type CancelUserEntityMemberInvitationArgs = {
  invitation: UserEntityMemberInvitation;
};

type AcceptUserEntityMemberInvitationArgs = {
  invitation: UserEntityMemberInvitation;
  session: Session;
  /**
   * How the invitee shows it is them. Signed in, the session is the proof and nothing else is sent.
   * Otherwise the token from the emailed link, with the registration on `invitation`.
   */
  acceptance: { signedIn: true } | { signedIn: false; token: string };
};

type ResendUserEntityMemberInvitationLinkArgs = {
  invitationId: Guid;
};

const jsonHeaders = {
  "Content-Type": "application/json",
};

export const userEntityMemberInvitationApi = api.injectEndpoints({
  endpoints: (build) => ({
    getActiveUserEntityMemberInvitations: build.query<
      Paginated<UserEntityMemberInvitation>,
      GetActiveUserEntityMemberInvitationsArgs
    >({
      async queryFn({ entityId, ...query }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: `legal-entities/${entityId.value}/user-entity-member-invitations`,
          params: userEntityMemberInvitationListParams(query),
        });

        if (response.error) {
          return {
            error: toQueryError(
              new UserEntityMemberInvitationAPIServiceError("getActiveUserEntityMemberInvitations", response.error),
            ),
          };
        }

        const invitations = (response.data as UserEntityMemberInvitationJSON[]).map((invitation) =>
          new UserEntityMemberInvitation(UserEntityMemberInvitationSchema.deserialize(invitation)),
        );
        // The page is the body; every total is in X-Pagination.
        const header = response.meta?.response?.headers.get("x-pagination") ?? "";
        return { data: { data: invitations, headers: PaginatedResponse.parsePaginationHeader(header) } };
      },
      providesTags: (_result, _error, { entityId }) => [
        { type: "UserEntityMemberInvitation" as const, id: `LegalEntity-${entityId.value}` },
      ],
    }),
    getUserEntityMemberInvitationById: build.query<UserEntityMemberInvitation, UserEntityMemberInvitationIdArgs>({
      async queryFn({ id }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery(`legal-entities/user-entity-member-invitations/${id.value}`);

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new UserMemberInvitationNotFoundError(id)) };
          }

          return {
            error: toQueryError(
              new UserEntityMemberInvitationAPIServiceError("getUserEntityMemberInvitationById", response.error),
            ),
          };
        }

        const params = UserEntityMemberInvitationSchema.deserialize(response.data as UserEntityMemberInvitationJSON);
        return { data: new UserEntityMemberInvitation(params) };
      },
    }),
    createUserEntityMemberInvitation: build.mutation<void, CreateUserEntityMemberInvitationArgs>({
      async queryFn({ invitation, accessTemplateId }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: `legal-entities/${invitation.userEntityInfo.entityId.value}/user-entity-member-invitations`,
          method: "POST",
          body: JSON.stringify(new CreateUserEntityMemberInvitationAPIRequest(invitation, accessTemplateId)),
          headers: jsonHeaders,
        });

        if (response.error) {
          return {
            error: toQueryError(
              new UserEntityMemberInvitationAPIServiceError("createUserEntityMemberInvitation", response.error, invitation),
            ),
          };
        }

        return { data: null as unknown as void };
      },
      invalidatesTags: (_result, _error, { invitation }) => [
        { type: "UserEntityMemberInvitation" as const, id: `LegalEntity-${invitation.userEntityInfo.entityId.value}` },
      ],
    }),
    cancelUserEntityMemberInvitation: build.mutation<void, CancelUserEntityMemberInvitationArgs>({
      async queryFn({ invitation }, _queryApi, _extraOptions, baseQuery) {
        if (!invitation.id) {
          return { error: toQueryError(new Error("Cannot cancel an invitation that has not been saved.")) };
        }

        const response = await baseQuery({
          url: `legal-entities/${invitation.userEntityInfo.entityId.value}/user-entity-member-invitations/${invitation.id.value}/cancel`,
          method: "POST",
        });

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new UserMemberInvitationNotFoundError(invitation.id)) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.Conflict) {
            return { error: toQueryError(new MemberInvitationNotOpenError(conflictMessage(response.error))) };
          }

          return {
            error: toQueryError(
              new UserEntityMemberInvitationAPIServiceError(
                "cancelUserEntityMemberInvitation",
                response.error,
                invitation,
              ),
            ),
          };
        }

        return { data: null as unknown as void };
      },
      invalidatesTags: (_result, _error, { invitation }) => [
        { type: "UserEntityMemberInvitation" as const, id: `LegalEntity-${invitation.userEntityInfo.entityId.value}` },
      ],
    }),
    acceptUserEntityMemberInvitation: build.mutation<void, AcceptUserEntityMemberInvitationArgs>({
      async queryFn({ invitation, session, acceptance }, _queryApi, _extraOptions, baseQuery) {
        if (!invitation.id) {
          return { error: toQueryError(new Error("Invitation ID is required to accept an invitation.")) };
        }
        if (!acceptance.signedIn && !invitation.registrationInfo?.password) {
          return { error: toQueryError(new Error("Password is required to accept an invitation.")) };
        }

        // Signed in, the session's bearer token is the proof and the account keeps its own name and
        // password, so the body is empty. Registering, the token from the emailed link rides with the
        // new account's name and password.
        const body = acceptance.signedIn
          ? {}
          : {
              ...new AcceptEntityUserInvitationAPIRequest(
                invitation.registrationInfo.password!,
                invitation.registrationInfo.name,
              ),
              token: acceptance.token,
            };
        const response = await baseQuery({
          url: `legal-entities/user-entity-member-invitations/${invitation.id.value}/accept`,
          method: "POST",
          body: JSON.stringify(body),
          headers: jsonHeaders,
        });

        if (response.error) {
          const refusal = readAcceptanceRefusal(response.error);
          if (refusal) return { error: toQueryError(refusal) };
          if (getApiErrorStatus(response.error) === HttpStatusCode.BadRequest) {
            return { error: toQueryError(new InvalidRegistrationInfoError(getApiErrorData(response.error))) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new UserMemberInvitationNotFoundError(invitation.id)) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.Conflict) {
            return { error: toQueryError(new MemberInvitationNotOpenError(conflictMessage(response.error))) };
          }

          return {
            error: toQueryError(
              new UserEntityMemberInvitationAPIServiceError(
                "acceptUserEntityMemberInvitation",
                response.error,
                invitation,
              ),
            ),
          };
        }

        // Accepting a member invitation is a login context and returns the shared auth payload.
        session.initializeFromAuthPayload(response.data);
        return { data: null as unknown as void };
      },
    }),
    resendUserEntityMemberInvitationLink: build.mutation<void, ResendUserEntityMemberInvitationLinkArgs>({
      async queryFn({ invitationId }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: `legal-entities/user-entity-member-invitations/${invitationId.value}/resend-link`,
          method: "POST",
        });

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new UserMemberInvitationNotFoundError(invitationId)) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.Conflict) {
            return { error: toQueryError(new MemberInvitationNotOpenError(conflictMessage(response.error))) };
          }
          return {
            error: toQueryError(
              new UserEntityMemberInvitationAPIServiceError("resendUserEntityMemberInvitationLink", response.error),
            ),
          };
        }

        return { data: null as unknown as void };
      },
    }),
  }),
});

export class UserEntityMemberInvitationAPIServiceError extends Error {
  invitation?: UserEntityMemberInvitation;
  method: string;
  error: unknown;

  constructor(method: string, error: unknown, invitation?: UserEntityMemberInvitation) {
    super(`UserEntityMemberInvitationAPIService: ${error instanceof Error ? error.message : String(error)}`);
    this.method = method;
    this.error = error;
    this.invitation = invitation;
  }
}

function toQueryError(error: Error): FetchBaseQueryError {
  return error as unknown as FetchBaseQueryError;
}

/**
 * The three ways acceptance refuses somebody who has not shown they are the invitee, each of which
 * the page answers differently: a fresh link, a sign-in, or a sign-out.
 */
function readAcceptanceRefusal(error: unknown): Error | undefined {
  const result = readApiError(error);
  if (!result.trusted) return undefined;
  switch (result.code) {
    case "member_invitation_link_invalid":
      return new InvitationLinkInvalidError();
    case "member_invitation_requires_sign_in":
      return new InvitationRequiresSignInError();
    case "member_invitation_for_another_account":
      return new InvitationForAnotherAccountError();
    default:
      return undefined;
  }
}

function conflictMessage(error: unknown): string | undefined {
  const result = readApiError(error);
  return result.trusted ? getApiErrorUserMessage(result) : undefined;
}

export const {
  useGetActiveUserEntityMemberInvitationsQuery,
  useGetUserEntityMemberInvitationByIdQuery,
  useLazyGetUserEntityMemberInvitationByIdQuery,
  useCreateUserEntityMemberInvitationMutation,
  useCancelUserEntityMemberInvitationMutation,
  useAcceptUserEntityMemberInvitationMutation,
  useResendUserEntityMemberInvitationLinkMutation,
} = userEntityMemberInvitationApi;
